import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy, ArrowLeft, Loader2, Building2, Clock, XCircle, Shield,
  TrendingUp, TrendingDown, BarChart3, AlertTriangle, Info, ChevronDown, ChevronUp, MapPin
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0));

const GRADE_COLORS = {
  A: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", fill: "#10b981" },
  B: { bg: "bg-sky-50 border-sky-200", text: "text-sky-700", fill: "#0ea5e9" },
  C: { bg: "bg-amber-50 border-amber-200", text: "text-amber-700", fill: "#f59e0b" },
  D: { bg: "bg-orange-50 border-orange-200", text: "text-orange-700", fill: "#f97316" },
  E: { bg: "bg-red-50 border-red-200", text: "text-red-700", fill: "#ef4444" },
};

export default function InsurerRankingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMethodology, setShowMethodology] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/log/insurer-rankings`, { headers: getHeaders() })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const rankings = data?.rankings || [];
  const market = data?.market_averages || {};
  const cityDelays = data?.city_delays || [];

  const chartData = rankings.slice(0, 10).map((r) => ({
    name: r.insurer_name.length > 15 ? r.insurer_name.slice(0, 15) + "..." : r.insurer_name,
    score: r.reliability_score,
    grade: r.grade,
  }));

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-slate-600" data-testid="rankings-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Classement IA des Assureurs
            </h1>
            <p className="text-sm text-slate-500">Analyse basée sur les données de toutes les pharmacies du réseau</p>
          </div>
        </div>

        {/* Methodology Section */}
        <Card className="bg-white border-slate-200 mb-8" data-testid="methodology-section">
          <CardContent className="p-5">
            <button onClick={() => setShowMethodology(!showMethodology)} 
              className="w-full flex items-center justify-between text-left" data-testid="methodology-toggle">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-sky-500" />
                <h3 className="text-sm font-bold text-slate-900">Methodologie du Score IA</h3>
              </div>
              {showMethodology ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showMethodology && (
              <div className="mt-4 space-y-4">
                <p className="text-xs text-slate-600">
                  Le score de fiabilite (0-100) est calcule a partir de <strong>3 parametres</strong>, ponderes selon leur importance pour les pharmacies :
                </p>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <p className="text-xs font-bold text-emerald-800">Taux de paiement (40%)</p>
                    </div>
                    <p className="text-[10px] text-emerald-700 leading-relaxed">
                      Pourcentage de factures effectivement payees par l'assureur (hors rejets). Plus ce taux est eleve, plus l'assureur est fiable.
                    </p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <p className="text-xs font-bold text-red-800">Taux de rejet (30%)</p>
                    </div>
                    <p className="text-[10px] text-red-700 leading-relaxed">
                      Pourcentage de factures rejetees. Un taux de rejet eleve penalise le score. Le score inverse est utilise : (100 - taux_rejet).
                    </p>
                  </div>
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-sky-600" />
                      <p className="text-xs font-bold text-sky-800">Delai de paiement (30%)</p>
                    </div>
                    <p className="text-[10px] text-sky-700 leading-relaxed">
                      Delai moyen entre le depot et le paiement. Un delai de 0j = 100 points, 66j+ = 0 points. Formule : max(0, 100 - delai x 1.5).
                    </p>
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-[10px] text-slate-600">
                    <strong>Echelle des grades :</strong> A (85-100) Excellent | B (70-84) Bon | C (55-69) Moyen | D (40-54) Faible | E (0-39) Critique
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Les donnees sont agregees a partir de <strong>toutes les pharmacies du reseau CMES</strong>, garantissant un classement objectif et representatif du marche ivoirien.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Market Averages */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="market-kpis">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-1">Assureurs classés</p>
              <p className="text-2xl font-bold text-slate-900">{rankings.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-1">Délai moyen marché</p>
              <p className="text-2xl font-bold text-sky-600">{market.avg_delay_days}j</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-1">Taux rejet moyen</p>
              <p className="text-2xl font-bold text-amber-600">{market.avg_rejection_rate}%</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-1">Fiabilité moyenne</p>
              <p className="text-2xl font-bold text-emerald-600">{market.avg_reliability_score}/100</p>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <Card className="bg-white border-slate-200 mb-8" data-testid="rankings-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-900">Score de fiabilité par assureur</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={120} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="score" name="Fiabilité" radius={[0, 4, 4, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={GRADE_COLORS[d.grade]?.fill || "#64748b"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Rankings table */}
        {/* City Delay Map */}
        {cityDelays.length > 0 && (
          <Card className="bg-white border-slate-200 mb-8" data-testid="city-delay-map">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-sky-500" />
                Cartographie des retards de paiement par ville
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Taille du cercle = volume de factures | Couleur = taux de recouvrement (vert = bon, rouge = critique)
              </p>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[420px] rounded-xl overflow-hidden border border-slate-200">
                <MapContainer
                  center={[7.0, -5.5]}
                  zoom={6}
                  scrollWheelZoom={false}
                  className="w-full h-full"
                  style={{ background: "#f8fafc" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  {cityDelays.map((city) => {
                    const radius = Math.max(8, Math.min(30, city.total_invoices * 2));
                    const rate = city.recovery_rate;
                    const color = rate >= 60 ? "#10b981" : rate >= 30 ? "#f59e0b" : "#ef4444";
                    return (
                      <CircleMarker
                        key={city.city}
                        center={[city.lat, city.lng]}
                        radius={radius}
                        fillColor={color}
                        fillOpacity={0.65}
                        color={color}
                        weight={2}
                        opacity={0.9}
                      >
                        <Popup>
                          <div className="text-xs min-w-[180px]">
                            <p className="font-bold text-sm mb-2 text-slate-900">{city.city}</p>
                            <div className="space-y-1">
                              <p><span className="text-slate-500">Pharmacies:</span> <strong>{city.pharmacies_count}</strong></p>
                              <p><span className="text-slate-500">Factures:</span> <strong>{city.total_invoices}</strong></p>
                              <p><span className="text-slate-500">Montant:</span> <strong>{fmt(city.total_amount)} FCFA</strong></p>
                              <p><span className="text-slate-500">En attente:</span> <strong className="text-amber-600">{city.pending_count}</strong></p>
                              <p><span className="text-slate-500">Recouvrement:</span> <strong className={rate >= 50 ? "text-emerald-600" : "text-red-600"}>{city.recovery_rate}%</strong></p>
                              <p><span className="text-slate-500">Delai estime:</span> <strong>{city.avg_delay_days}j</strong></p>
                            </div>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              </div>
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-[10px] text-slate-600">Recouvrement &gt; 60%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-[10px] text-slate-600">30-60%</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-[10px] text-slate-600">&lt; 30%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rankings table - Classement detail */}
        <div className="space-y-3" data-testid="rankings-list">
          {rankings.map((r) => {
            const gc = GRADE_COLORS[r.grade] || GRADE_COLORS.C;
            return (
              <Card key={r.insurer_name} className="bg-white border-slate-200 overflow-hidden" data-testid={`ranking-${r.rank}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    {/* Rank badge */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg border ${gc.bg} ${gc.text}`}>
                      {r.rank <= 3 ? <Trophy className="w-5 h-5" /> : `#${r.rank}`}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-slate-900">{r.insurer_name}</h3>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${gc.bg} ${gc.text}`}>
                          {r.grade} — {r.grade_label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {r.total_invoices} factures | {r.pharmacies_count} pharmacie(s) | {fmt(r.total_amount)} FCFA
                      </p>
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${gc.text}`}>{r.reliability_score}</p>
                      <p className="text-xs text-slate-500">/100</p>
                    </div>
                  </div>

                  {/* Metrics bar with market comparison */}
                  <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Delai moyen</p>
                      <p className="text-sm font-bold text-slate-900 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-sky-500" />
                        {r.avg_delay_days}j
                        {r.avg_delay_days > market.avg_delay_days ? (
                          <TrendingDown className="w-3 h-3 text-red-400" />
                        ) : (
                          <TrendingUp className="w-3 h-3 text-emerald-400" />
                        )}
                      </p>
                      <p className={`text-[9px] mt-0.5 ${r.avg_delay_days <= market.avg_delay_days ? 'text-emerald-500' : 'text-red-500'}`}>
                        {r.avg_delay_days <= market.avg_delay_days ? `${market.avg_delay_days - r.avg_delay_days}j < marche` : `+${r.avg_delay_days - market.avg_delay_days}j vs marche`}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Taux rejet</p>
                      <p className="text-sm font-bold text-slate-900 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                        {r.rejection_rate}%
                      </p>
                      <p className={`text-[9px] mt-0.5 ${r.rejection_rate <= market.avg_rejection_rate ? 'text-emerald-500' : 'text-red-500'}`}>
                        {r.rejection_rate <= market.avg_rejection_rate ? 'Sous la moyenne' : 'Au-dessus moyenne'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Taux paiement</p>
                      <p className="text-sm font-bold text-slate-900 flex items-center gap-1">
                        <Shield className="w-3.5 h-3.5 text-emerald-500" />
                        {r.payment_rate}%
                      </p>
                      <p className={`text-[9px] mt-0.5 ${r.payment_rate >= 50 ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {r.payment_rate >= 50 ? 'Bon payeur' : 'A surveiller'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Fiabilite vs marche</p>
                      <p className="text-sm font-bold text-slate-900 flex items-center gap-1">
                        <BarChart3 className="w-3.5 h-3.5 text-violet-500" />
                        {r.reliability_score > market.avg_reliability_score ? '+' : ''}{(r.reliability_score - market.avg_reliability_score).toFixed(1)}
                      </p>
                      <p className={`text-[9px] mt-0.5 ${r.reliability_score >= market.avg_reliability_score ? 'text-emerald-500' : 'text-red-500'}`}>
                        {r.reliability_score >= market.avg_reliability_score ? 'Superieur au marche' : 'Inferieur au marche'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {rankings.length === 0 && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-12 text-center">
                <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Pas assez de données pour générer un classement</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
